import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["actions", "content", "id", "onVisibilityDurationEnd", "position", "style", "variant", "visibilityDuration"],
    _excluded2 = ["onClick", "text", "variant"];

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

import React, { useCallback, useEffect } from 'react';
import { BUTTON_VARIANTS } from "../../button";
import { useShareForwardedRef } from "../../utilities";
import PropTypes from 'prop-types';
import { ToastIconContainer } from "../blocks";
import { MAX_ALLOWED_ACTIONS, TOAST_POSITIONS, TOAST_VARIANTS, TOAST_VISIBILITY_DURATIONS } from "../constants";
import { ToastActions, ToastBody, ToastButton, ToastContent, ToastWrapper } from "../elements";
import { useSwipe } from "../utilities";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var useToastVisibility = function useToastVisibility(_ref) {
  var id = _ref.id,
      onVisibilityDurationEnd = _ref.onVisibilityDurationEnd,
      ref = _ref.ref,
      visibilityDuration = _ref.visibilityDuration;
  var handleDismiss = useCallback(function (event) {
    onVisibilityDurationEnd(event, id);
  }, [id, onVisibilityDurationEnd]);
  useEffect(function () {
    var visibilityTimer = visibilityDuration !== TOAST_VISIBILITY_DURATIONS.INFINITE ? setTimeout(function () {
      return handleDismiss();
    }, visibilityDuration) : null;
    return function () {
      return clearTimeout(visibilityTimer);
    };
  }, [handleDismiss, visibilityDuration]);
  var swipeStyles = useSwipe(ref, onVisibilityDurationEnd, id);
  return {
    swipeStyles: swipeStyles
  };
};

var Toast = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var actions = _ref2.actions,
      content = _ref2.content,
      id = _ref2.id,
      onVisibilityDurationEnd = _ref2.onVisibilityDurationEnd,
      position = _ref2.position,
      style = _ref2.style,
      variant = _ref2.variant,
      visibilityDuration = _ref2.visibilityDuration,
      other = _objectWithoutProperties(_ref2, _excluded);

  var actionsCount = actions && actions.length || 0;
  var hasActions = actionsCount > 0;
  var hasMultipleActions = actionsCount > 1;
  var toastRef = useShareForwardedRef(ref);

  var _useToastVisibility = useToastVisibility({
    id: id,
    onVisibilityDurationEnd: onVisibilityDurationEnd,
    ref: toastRef,
    visibilityDuration: visibilityDuration
  }),
      swipeStyles = _useToastVisibility.swipeStyles;

  var renderToastActions = function renderToastActions() {
    return /*#__PURE__*/_jsx(ToastActions, {
      withMultipleActions: hasMultipleActions,
      children: actions.slice(0, MAX_ALLOWED_ACTIONS).map(function (actionProps) {
        var onClick = actionProps.onClick,
            text = actionProps.text,
            buttonVariant = actionProps.variant,
            otherActionProps = _objectWithoutProperties(actionProps, _excluded2);

        var buttonAppearance = buttonVariant === BUTTON_VARIANTS.PRIMARY ? buttonVariant : BUTTON_VARIANTS.TEXT;

        var handleClick = function handleClick(event) {
          onClick(event, id);
        };

        return /*#__PURE__*/_jsx(ToastButton, _objectSpread({
          "aria-controls": id,
          onClick: handleClick,
          preserveClickableArea: true,
          text: text,
          variant: buttonAppearance,
          withMultipleActions: hasMultipleActions
        }, otherActionProps), text);
      })
    });
  };

  return /*#__PURE__*/_jsxs(ToastWrapper, _objectSpread(_objectSpread({
    id: id,
    position: position,
    ref: toastRef,
    style: _objectSpread(_objectSpread({}, style), swipeStyles),
    variant: variant
  }, other), {}, {
    children: [/*#__PURE__*/_jsx(ToastIconContainer, {
      variant: variant
    }), /*#__PURE__*/_jsxs(ToastBody, {
      variant: variant,
      withMultipleActions: hasMultipleActions,
      children: [/*#__PURE__*/_jsx(ToastContent, {
        withMultipleActions: hasMultipleActions,
        children: content
      }), hasActions && renderToastActions()]
    })]
  }));
});
Toast.propTypes = {
  /** Array of action elements. Action element contains Button component properties.
   * A maximum of 2 actions is allowed. */
  actions: PropTypes.arrayOf(PropTypes.shape({
    /** Changes button style depending on variant */
    variant: PropTypes.oneOf([BUTTON_VARIANTS.PRIMARY, BUTTON_VARIANTS.TEXT])
  })),

  /** The content of the component */
  content: PropTypes.node.isRequired,

  /** Unique id for component */
  id: PropTypes.string.isRequired,

  /** Toast visibility handler when visibility duration ends */
  onVisibilityDurationEnd: PropTypes.func,

  /** Sets component position accordingly to specified value */
  position: PropTypes.oneOf(Object.values(TOAST_POSITIONS)),

  /** Inline styles applied to main component wrapper */
  style: PropTypes.shape({}),

  /** Changes toast style depending on variant. */
  variant: PropTypes.oneOf(Object.values(TOAST_VARIANTS)),

  /** Duration of time for how long component will be visible */
  visibilityDuration: PropTypes.oneOf(Object.values(TOAST_VISIBILITY_DURATIONS))
};
Toast.defaultProps = {
  actions: undefined,
  onVisibilityDurationEnd: function onVisibilityDurationEnd() {},
  position: TOAST_POSITIONS.INLINE,
  style: {},
  variant: TOAST_VARIANTS.NEUTRAL,
  visibilityDuration: TOAST_VISIBILITY_DURATIONS.SEVEN_SECONDS
};
export { Toast };